import APIHelpers from '../apiHelpers';
import { mockCart } from './mocks/mockCart';
import { mockProduct } from './mocks/mockProduct';
import { CartLineItem, ShopifyCartAddToCartResponse, ShopifyCartState } from './types';

class ShopifyClient {
  apiHelpers: APIHelpers;

  constructor() {
    this.apiHelpers = new APIHelpers('');
  }

  getCurrentVariantId = (): number | undefined => {
    if (window.location.hostname === 'localhost') {
      return 42053137268895;
    }

    const params = new URLSearchParams(window.location.search);

    return params.get('variant') ? parseInt(params.get('variant')!) : undefined;
  };

  getCurrentQuantity = (): number => {
    const quantityElement = <HTMLInputElement>document.querySelector('.quantity input');

    if (quantityElement && quantityElement.value && !isNaN(parseInt(quantityElement.value))) {
      return parseInt(quantityElement.value);
    }
    return 1;
  };

  getCurrentProduct = async (): Promise<any> => {
    if (window.location.hostname === 'localhost') {
      return mockProduct;
    }

    const browserProduct = await this.apiHelpers.fetch(
      `https://${window.location.hostname}${window.location.pathname}.js`
    );
    return browserProduct;
  };

  getProduct = async (handle: string): Promise<any> => {
    if (window.location.hostname === 'localhost') {
      return mockProduct;
    }

    const product = await this.apiHelpers.fetch(
      `https://${window.location.hostname}/products/${handle}.js`
    );

    return product;
  };

  getCurrentShop = (): string => {
    if (window.location.hostname === 'localhost') {
      return 'kevdevkev';
    }

    // @ts-ignore - gives back like kevdevkev.myshopify.com
    return window.Shopify.shop.split('.')[0];
  };

  getBrowserCart = async (): Promise<ShopifyCartState> => {
    if (window.location.hostname === 'localhost') {
      return mockCart as unknown as ShopifyCartState;
    }
    const browserCart = await this.apiHelpers.fetch(
      `${window.location.origin}${(window as any).Shopify.routes.root}cart.js`
    );
    // TODO: Remove once migration is complete
    for (const item of (browserCart as ShopifyCartState).items) {
      if (item.properties && item.properties._isTokenGated && !item.properties._network) {
        item.properties._network = 1;
      }
    }
    return browserCart as ShopifyCartState;
  };

  addToBrowserCart = async (
    variantId: string,
    quantity: number,
    cartIconSection: string | undefined,
    productProperties: Array<string> | undefined
  ): Promise<ShopifyCartState> => {
    if (window.location.hostname === 'localhost') {
      return mockCart as unknown as ShopifyCartState;
    }

    let properties: { [key: string]: string } | undefined = undefined;
    if (productProperties) {
      properties = {};
      for (const productProperty of productProperties) {
        const propertyElement = document.getElementById(productProperty) as HTMLInputElement;
        if (propertyElement && propertyElement.value) {
          let propertyName = propertyElement.name;
          const propertyNameRegex = propertyName.match(/^properties\[(.*)\]$/);
          if (propertyNameRegex) {
            propertyName = propertyNameRegex[1];
          }
          properties[propertyName] = propertyElement.value.trim();
        }
      }
    }

    const body = {
      items: [
        properties
          ? {
              id: variantId,
              quantity,
              properties
            }
          : {
              id: variantId,
              quantity
            }
      ],
      sections: cartIconSection
    };
    const browserCart = await this.apiHelpers.post(
      `${window.location.origin}${(window as any).Shopify.routes.root}cart/add.js`,
      {
        body: body
      }
    );
    return browserCart as ShopifyCartState;
  };

  addToBrowserCartWithProperties = async (
    items: any,
    cartIconSection: string | undefined
  ): Promise<ShopifyCartAddToCartResponse> => {
    if (window.location.hostname === 'localhost') {
      return mockCart as unknown as ShopifyCartAddToCartResponse;
    }

    const body = {
      items: items,
      sections: cartIconSection
    };
    try {
      const browserCart = await this.apiHelpers.post(
        `${window.location.origin}${(window as any).Shopify.routes.root}cart/add.js`,
        {
          body: body
        }
      );
      return browserCart;
    } catch (e: any) {
      if ([404, 422].includes(e.status)) {
        // Could not add to browser cart because one or more items does not exist or is sold out
        // We need to clear the cart
        // TODO: Gracefully handle this so that only certain items are removed
        return await this.clearBrowserCart(cartIconSection);
      }
      throw e;
    }
  };

  updateBrowserCart = async (
    cartUpdates: any,
    attributes: any,
    cartIconSection: string | undefined
  ): Promise<ShopifyCartAddToCartResponse> => {
    if (window.location.hostname === 'localhost') {
      return mockCart as unknown as ShopifyCartAddToCartResponse;
    }

    const body = {
      updates: cartUpdates,
      sections: cartIconSection,
      attributes
    };
    const browserCart = await this.apiHelpers.post(
      `${window.location.origin}${(window as any).Shopify.routes.root}cart/update.js`,
      {
        body: body
      }
    );

    return browserCart as ShopifyCartAddToCartResponse;
  };

  changeBrowserCart = async (
    key: string,
    properties: CartLineItem['properties'] | undefined,
    quantity: number,
    cartIconSection: string | undefined
  ): Promise<ShopifyCartAddToCartResponse> => {
    if (window.location.hostname === 'localhost') {
      return mockCart as unknown as ShopifyCartAddToCartResponse;
    }

    const body = {
      id: key,
      quantity,
      properties,
      sections: cartIconSection
    };
    const browserCart = await this.apiHelpers.post(
      `${window.location.origin}${(window as any).Shopify.routes.root}cart/change.js`,
      {
        body: body
      }
    );

    return browserCart as ShopifyCartAddToCartResponse;
  };

  clearBrowserCart = async (
    cartIconSection: string | undefined
  ): Promise<ShopifyCartAddToCartResponse> => {
    return await this.apiHelpers.post(
      `${window.location.origin}${(window as any).Shopify.routes.root}cart/clear.js`,
      {
        body: {
          sections: cartIconSection
        }
      }
    );
  };
}

export default ShopifyClient;
